import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {ChevronDown, ChevronRight} from "lucide-react";
import {get} from "lodash";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {callDateParts} from "business/upcomingCalls";
import {color, font, radius, type} from "common/tokens";

const useStyles = makeStyles(theme => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  eyebrow: {
    ...type.eyebrow,
    letterSpacing: '1.1px',
    color: color.TEXT_MUTED,
    margin: 0,
  },
  divider: {
    height: 1,
    background: color.DIVIDER,
    width: '100%',
  },
  // A collapsible section's label is its own toggle, so the header carries the
  // chevron rather than adding a second control beside it.
  toggle: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    cursor: 'pointer',
    color: color.TEXT_MUTED,
  },
  chevronToggle: {
    display: 'flex',
    transition: 'transform 180ms ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '19px 0',
    borderBottom: `1px solid ${color.DIVIDER}`,
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  rowPast: {
    opacity: 0.7,
  },
  // Today's call is a filled card rather than a list row.
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 29,
    padding: 20,
    borderRadius: radius.CTA,
    background: color.ACCENT_TINT,
    border: `1px solid ${color.DIVIDER}`,
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      gap: 16,
    },
  },
  rowButton: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${color.DIVIDER}`,
    font: 'inherit',
    cursor: 'pointer',
    textAlign: 'left',
  },
  dateTile: {
    width: 46,
    flexShrink: 0,
    textAlign: 'center',
  },
  month: {
    ...type.eyebrow,
    display: 'block',
    letterSpacing: '0.66px',
    color: color.DANGER,
    margin: 0,
  },
  monthMuted: {
    color: color.TEXT_MUTED,
  },
  day: {
    display: 'block',
    fontFamily: font.SANS,
    fontSize: 22,
    lineHeight: '26px',
    fontWeight: font.BOLD,
    color: color.TEXT,
    margin: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: '1 0 0',
    minWidth: 0,
    // A real basis lets the actions wrap onto their own line when they
    // don't fit beside the title, instead of squeezing it to nothing.
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 160px',
    },
  },
  title: {
    ...type.body,
    fontWeight: font.MEDIUM,
    color: color.TEXT,
    margin: 0,
  },
  titleCard: {
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: font.SEMIBOLD,
  },
  meta: {
    ...type.meta,
    lineHeight: '20px',
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  liveChip: {
    ...type.chip,
    alignSelf: 'flex-start',
    background: color.DANGER_TINT,
    color: color.DANGER,
    borderRadius: 20,
    padding: '3px 10px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
    paddingRight: 24,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      flexShrink: 1,
      paddingRight: 0,
    },
  },
  actionsPast: {
    gap: 16,
  },
  chevron: {
    color: color.TEXT_MUTED,
    display: 'flex',
    flexShrink: 0,
  },
  // The row and the details it opens are one block, so the divider under the
  // row moves to the foot of the details.
  rowOpen: {
    borderBottom: 'none',
  },
  details: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    padding: '0 0 19px',
    borderBottom: `1px solid ${color.DIVIDER}`,
  },
}));

// One row shape for every event list: the home's upcoming calls and each
// section of All Events. `renderActions` supplies the buttons a section needs;
// `renderDetails` puts them under the row instead, revealed by clicking it;
// without either the row itself is the click target.
const EventList = ({
  label,
  events = [],
  variant = 'row',
  renderActions,
  renderDetails,
  onOpenEvent,
  isLive,
  meta,
  footer,
  showDivider = false,
  collapsible = false,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [openEventId, setOpenEventId] = useState(null);

  if (!events.length) {
    return null;
  }

  const isCard = variant === 'card';
  const isPast = variant === 'past';

  return (
    <section className={classes.section}>
      {label && (collapsible ? (
        <button
          type={'button'}
          className={classes.toggle}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          <span className={classes.eyebrow}>{label}</span>
          <span className={classNames(classes.chevronToggle, expanded && classes.chevronOpen)}>
            <ChevronDown size={16} strokeWidth={2}/>
          </span>
        </button>
      ) : (
        <p className={classes.eyebrow}>{label}</p>
      ))}
      {showDivider && <span className={classes.divider}/>}

      {(!collapsible || expanded) && (
      <ul className={classNames(classes.list, collapsible && 'ui-enter')}>
        {events.map(event => {
          const {month, day, time} = callDateParts(event);
          const isOpen = renderDetails && openEventId === get(event, 'id');

          const content = (
            <>
              <span className={classes.dateTile}>
                <span className={classNames(classes.month, (isPast || isCard) && classes.monthMuted)}>
                  {month}
                </span>
                <span className={classes.day}>{day}</span>
              </span>
              <span className={classes.body}>
                {isLive && isLive(event) && (
                  <span className={classes.liveChip}>{t('liveNow')}</span>
                )}
                <span className={classNames(classes.title, isCard && classes.titleCard)}>
                  {get(event, 'title')}
                </span>
                <span className={classes.meta}>{meta ? meta(event) : time}</span>
              </span>
              {renderActions
                ? (
                  <span className={classNames(classes.actions, isPast && classes.actionsPast)}>
                    {renderActions(event)}
                  </span>
                )
                : renderDetails
                  ? (
                    <span className={classNames(classes.chevron, classes.chevronToggle, isOpen && classes.chevronOpen)}>
                      <ChevronDown size={16} strokeWidth={2}/>
                    </span>
                  )
                  : <span className={classes.chevron}><ChevronRight size={16} strokeWidth={2}/></span>}
            </>
          );

          const shell = classNames(
            isCard ? classes.card : classes.row,
            isPast && classes.rowPast
          );

          return (
            <li key={get(event, 'id')}>
              {renderActions ? (
                <div className={shell}>{content}</div>
              ) : (
                <button
                  type={'button'}
                  className={classNames(shell, classes.rowButton, isOpen && classes.rowOpen)}
                  aria-expanded={renderDetails ? Boolean(isOpen) : undefined}
                  onClick={() => renderDetails
                                  ? setOpenEventId(isOpen ? null : get(event, 'id'))
                                  : onOpenEvent && onOpenEvent(event)}
                >
                  {content}
                </button>
              )}
              {isOpen && (
                <div className={classNames(classes.details, 'ui-enter')}>{renderDetails(event)}</div>
              )}
            </li>
          );
        })}
      </ul>
      )}

      {footer}
    </section>
  );
};

export default EventList;
